import React, {useEffect, useMemo, useState, useCallback} from 'react';
import {useStores} from '@src/components/store';
import CustomSelect from '@src/components/shared/custom-select/custom-select';
import {ConnectionItemRenderer, serviceComparatorByName} from '@src/components/shared/label-renderer/label-renderer';
import {ExternalIpsByServiceId, ServiceDto} from '@src/dtos/customer.dto';
import customerService from '@src/services/customer.service';
import Loader from '@src/components/shared/loader/loader';
import CopyButton from '@src/components/shared/copy-button/copy-button';
import {triggerFileDownload} from '@src/utils/activity-util';
import './public-ips-tab.scss';

const ERROR_LOADING_IPS = 'Failed to load public IPs';
export const SCROLLBAR_THRESHOLD = 4;

const ConnectionLabelRenderer = (props: any) => <ConnectionItemRenderer {...props} displayCircuitType={false} />;

const PublicIpsTab: React.FC = () => {
    const { customerStore } = useStores();
    const [customerIps, setCustomerIps] = useState<ExternalIpsByServiceId[]>([]);
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const customerServices = useMemo(() =>
        ((customerStore.selectedCustomer?.services ?? []).slice()
            .sort(serviceComparatorByName))
            .map(_ => {
                return { key: _.serviceId, value: _.serviceId, label: _.serviceName || _.adiCircuitId, data: _ };
            }), [customerStore.selectedCustomer]);

    const [selectedConnection, setSelectedConnection] = useState<ServiceDto | undefined>(undefined);

    useEffect(() => {
        if (customerStore.selectedLocation) {
            setSelectedConnection(customerStore.selectedLocation);
        } else {
            setSelectedConnection(customerServices.length > 0 ? customerServices[0].data : undefined);
        }
    }, [customerStore.selectedLocation, customerServices]);

    const handleSelectConnection = (serviceId: number) => {
        const connection = customerServices.find(conn => conn.value === serviceId)?.data;
        if (connection) {
            setSelectedConnection(connection);
        }
    };

    useEffect(() => {
        setCustomerIps([]);
        setError(null);
        if (customerStore.customerId) {
            const getExternalIpsByCustomerId = async () => {
                setLoading(true);
                try {
                    const customerIps = await customerService.getExternalIpsByCustomerId(customerStore.customerId);
                    setCustomerIps(customerIps ?? []);
                } catch (err) {
                    console.log(`${ERROR_LOADING_IPS} with error: `, err);
                    setError(ERROR_LOADING_IPS);
                    setCustomerIps([]);
                } finally {
                    setLoading(false);
                }
            };
            void getExternalIpsByCustomerId();
        }
    }, [customerStore.customerId]);

    const ipListElement = useMemo(() => {
        if (selectedConnection?.serviceId) {
            const serviceIps = customerIps.find(customerIp => customerIp.serviceId === selectedConnection.serviceId);
            if (serviceIps) {
                const allIps = [...serviceIps.externalIpsDto.ipv4Subnets, ...serviceIps.externalIpsDto.ipv6Subnets];
                return (
                    <div className={`ip-item-list ${allIps.length > SCROLLBAR_THRESHOLD ? 'has-scrollbar' : ''}`}>
                        {
                            allIps.map(ip => (
                                <div className="ip-item-group" key={ip}>
                                    <div className="ip-item">{ip}</div>
                                    <div className="ip-item-copy">
                                        <CopyButton value={ip}/>
                                    </div>
                                </div>
                            ))
                        }
                    </div>
                );
            }
        }
    }, [customerIps, selectedConnection]);

    const onExportClick = useCallback(() => {
        setExporting(true);
        let csvRows: string[] = [];
        customerServices.forEach(conn => {
            const serviceIps = customerIps.find(customerIp => customerIp.serviceId === conn.value);
            csvRows.push(conn.label, 'IPv4');
            csvRows = csvRows.concat(serviceIps?.externalIpsDto.ipv4Subnets || []);
            csvRows.push('IPv6');
            csvRows = csvRows.concat(serviceIps?.externalIpsDto.ipv6Subnets || []);
            csvRows.push('');
        });
        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], {type: 'text/csv;charset=utf-8;'});
        triggerFileDownload(blob, `public_ips_${customerStore.selectedCustomer?.customerName ?? ''}.csv`);
        setExporting(false);
    }, [customerServices, customerIps, customerStore.selectedCustomer]);

    const exportSectionElement = useMemo(() => {
        return (
            <div className="export-ips-container">
                <div className="tab-title">
                    Export external IPs for all connection or view specific connection IPs below
                </div>
                <button type="button" className={`custom-btn secondary-btn ${exporting ? ' loading' : ''}`} onClick={onExportClick}
                        disabled={loading || exporting || customerServices.length === 0 || customerIps.length === 0}>
                    Export All
                </button>
            </div>
        );
    }, [exporting, loading, onExportClick, customerServices, customerIps]);

    return (
        <div className="public-ips-tab">
            {exportSectionElement}
            <CustomSelect placeholder={'Select Connection'}
                          onSelect={handleSelectConnection}
                          value={selectedConnection?.serviceId}
                          disabled={loading || exporting || customerServices.length === 0}
                          options={customerServices}
                          emptyMessage={'No connection found'}
                          LabelRenderer={ConnectionLabelRenderer}/>
            {loading && <Loader type={'full-screen'}/>}
            {error && <div className="ip-error">{error}</div>}
            {!loading && !error && ipListElement}
        </div>
    );
};

export default PublicIpsTab;

